import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import GameScreenLayout from "@/components/GameScreenLayout"
import {
  applyToGuild,
  claimGuildChest,
  getGuildList,
  getGuildMine,
  joinGuildByInvite,
  leaveGuild,
} from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

export default function GuildPage() {
  const queryClient = useQueryClient()
  const { syncGameState } = useGuardiansPassport()
  const [inviteCode, setInviteCode] = useState("")

  const mineQuery = useQuery({
    queryKey: ["guild-mine"],
    queryFn: getGuildMine,
  })

  const listQuery = useQuery({
    queryKey: ["guild-list"],
    queryFn: getGuildList,
    enabled: !mineQuery.data?.membership,
  })

  const membership = mineQuery.data?.membership
  const hub = mineQuery.data?.hub

  const apply = useMutation({
    mutationFn: (guildId: string) => applyToGuild(guildId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["guild-list"] })
    },
  })

  const joinInvite = useMutation({
    mutationFn: () => joinGuildByInvite(inviteCode.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["guild-mine"] })
      setInviteCode("")
    },
  })

  const claimChest = useMutation({
    mutationFn: () => claimGuildChest(),
    onSuccess: async () => {
      await syncGameState()
      void queryClient.invalidateQueries({ queryKey: ["guild-mine"] })
    },
  })

  const leave = useMutation({
    mutationFn: () => leaveGuild(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["guild-mine"] })
      void queryClient.invalidateQueries({ queryKey: ["guild-list"] })
    },
  })

  return (
    <GameScreenLayout title="Гильдия" backTo="/play">
      {mineQuery.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : membership && hub ? (
        <div className="space-y-4">
          <header className="rounded-xl border border-white/10 bg-white/5 p-4">
            <p className="text-lg font-semibold">
              [{hub.guild.tag}] {hub.guild.name}
            </p>
            <p className="text-xs text-guardians-muted">
              Ур. {hub.guild.level} · {hub.guild.membersCount}/
              {hub.guild.maxMembers} · роль {hub.myRole}
            </p>
            {hub.guild.announcement ? (
              <p className="mt-2 text-sm text-guardians-muted">
                {hub.guild.announcement}
              </p>
            ) : null}
          </header>

          {hub.canClaimChest ? (
            <button
              type="button"
              disabled={claimChest.isPending}
              onClick={() => claimChest.mutate()}
              className="w-full rounded-xl bg-guardians-accent py-2 text-sm font-medium text-guardians-bg"
            >
              Забрать сундук гильдии
            </button>
          ) : null}

          <button
            type="button"
            disabled={leave.isPending}
            onClick={() => leave.mutate()}
            className="w-full rounded-xl border border-red-500/40 py-2 text-sm text-red-200"
          >
            Покинуть гильдию
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <section className="rounded-xl border border-white/10 p-3">
            <p className="mb-2 text-sm font-medium">Вступить по инвайту</p>
            <div className="flex gap-2">
              <input
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Код приглашения"
                className="min-w-0 flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
              />
              <button
                type="button"
                disabled={!inviteCode.trim() || joinInvite.isPending}
                onClick={() => joinInvite.mutate()}
                className="rounded-lg bg-guardians-accent px-3 py-2 text-xs font-medium text-guardians-bg"
              >
                Войти
              </button>
            </div>
          </section>

          {listQuery.isLoading ? (
            <p className="text-sm text-guardians-muted">Список гильдий…</p>
          ) : (
            <ul className="space-y-2">
              {(listQuery.data ?? []).map((g) => (
                <li
                  key={g.id}
                  className="flex items-center justify-between gap-2 rounded-xl border border-white/10 p-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      [{g.tag}] {g.name}
                    </p>
                    <p className="text-xs text-guardians-muted">
                      Ур.{g.level} · {g.membersCount}/{g.maxMembers}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={apply.isPending || g.myApplicationStatus === "PENDING"}
                    onClick={() => apply.mutate(g.id)}
                    className="shrink-0 rounded-lg border border-white/20 px-2 py-1 text-xs"
                  >
                    {g.myApplicationStatus === "PENDING" ? "Заявка" : "Подать"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {(apply.isError || joinInvite.isError || leave.isError) && (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(
            apply.error ?? joinInvite.error ?? leave.error,
            "Ошибка гильдии"
          )}
        </p>
      )}
    </GameScreenLayout>
  )
}
